// Utility to get descriptions for enums
import {GUID, VivoPortal} from "./AppConstants";
import EnumMemberGroupAliases = VivoPortal.EnumMemberGroupAliases;
import {DropDownListItem} from "./Models";
import * as icons from "@fortawesome/free-solid-svg-icons";
import {IconDefinition} from "@fortawesome/free-regular-svg-icons";
import {useParams} from "react-router-dom";


export namespace CommonUtils {
    import EnumOrderListFilter = VivoPortal.EnumOrderListFilter;
    import EnumSegment = VivoPortal.EnumSegment;
    import EnumOrderStatus = VivoPortal.EnumOrderStatus;
    import EnumOrderStatusDescriptions = VivoPortal.EnumOrderStatusDescriptions;
    import EnumOrderStatusIcons = VivoPortal.EnumOrderStatusIcons;
    import languageList = VivoPortal.languageList;

    const StoredPredefinedFilterKey = "StoredPredefinedFilter";

    export function getCurrentLanguage(lng?: string) {
        lng ??= getLng();
        return languageList.filter(x => x.IdString == lng)[0] ?? languageList[0];
    }

    export function getLng() {
        return localStorage.getItem("i18nextLng") ?? "en";
    }

    export function getEnumMemberGroupDescription(value: EnumMemberGroupAliases): string {
        switch (value) {
            case EnumMemberGroupAliases.Admin:
                return 'Admin';
            case EnumMemberGroupAliases.Customer:
                return 'Customer';
            case EnumMemberGroupAliases.Employee:
                return 'FG Employee';
            case EnumMemberGroupAliases.Supervisor:
                return 'Laboratory Employee';
            case EnumMemberGroupAliases.User:
                return 'Portal user';
            case EnumMemberGroupAliases.InactiveUser:
                return 'Inactive user';
            case EnumMemberGroupAliases.Observer:
                return 'Observer';
            case EnumMemberGroupAliases.SuperAdmin:
                return 'Super Admin';
            default:
                return 'Unknown';
        }
    }

    export function getEnumSegmentDescription(value: EnumSegment): string {
        switch (value) {
            case EnumSegment.PaperAndBoard:
                return 'Paper & Board';
            case EnumSegment.Sheetfed:
                return 'Sheetfed';
            case EnumSegment.Narrow_Web:
                return 'Narrow Web';
            case EnumSegment.Flexible_Packaging:
                return 'Flexible Packaging';
            default:
                return 'Unknown';
        }
    }

// Function to get the description of a specific status
    export function getEnumOrderStatusDescription(status: EnumOrderStatus): string {
        return EnumOrderStatusDescriptions[status] || "UNKNOWN_STATUS";
    }

    export function getEnumOrderStatusIcon(status: EnumOrderStatus): icons.IconDefinition {
        return EnumOrderStatusIcons[status] || icons.faQuestion;
    }

    export function NewGuid(): GUID {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }
    
    export function getMinId<T extends {Id:number}>(collection: T[]): number {
        if (collection.length == 0) return 0;
        return collection.reduce((min, item) =>
            item.Id < min ? item.Id : min, collection[0]?.Id || Infinity);
    }
    export function getParameterStoredPredefinedFilter(): EnumOrderListFilter {
        // Default value for the enum
        let result: EnumOrderListFilter = EnumOrderListFilter.MyAll;

        // Retrieve the stored value from localStorage
        let predefinedFilter = localStorage.getItem(StoredPredefinedFilterKey);
        if (!predefinedFilter) {
            // If no value is stored, save the default and return it
            setParameterStoredPredefinedFilter(result);
            return result;
        }

        // Parse the stored value to an integer
        let stored = parseInt(predefinedFilter, 10);
        if (!isNaN(stored) && EnumOrderListFilter[stored] !== undefined) {
            // If the parsed value is valid, return it as the enum
            return stored as EnumOrderListFilter;
        }

        // Fallback to the default value if parsing fails
        setParameterStoredPredefinedFilter(result);
        return result;
    }

    export function setParameterStoredPredefinedFilter(filter: EnumOrderListFilter): void {
        // Store the integer representation of the enum
        localStorage.setItem(StoredPredefinedFilterKey, filter.toString());
    }

    export function ConvertEnumToList<T extends object>(enumType: T) {
        let result: DropDownListItem[] = Object.keys(enumType)
            .filter((key) => !isNaN(Number(key))) // Filter out the numeric keys
            .map((key) => (
                {
                    Name: (enumType as any)[Number(key)],
                    Id: Number(key)
                }
            ));
        return result;
    }

    export function scrollToClassElement(className: string, container: HTMLElement = document.body, topOffset: number = 100) {
        if (!className.startsWith('.'))
            className = '.' + className;
        // Find the first element with the selected class inside the container
        const invalid = container.querySelector(className);

        if (!invalid) {
            return; // No elements, exit the function
        }
        // Calculate the scroll target position
        const targetPosition = invalid.getBoundingClientRect().top + window.scrollY - topOffset;

        // Smooth scroll to the target position
        window.scrollTo({
            top: targetPosition,
            behavior: 'smooth', // Smooth scrolling equivalent to jQuery's animate,
        });
    }

    export function scrollToTopOfThePage(topOffset: number = 100) {
        // Smooth scroll to the target position
        window.scrollTo({
            top: topOffset,
            behavior: 'smooth', // Smooth scrolling equivalent to jQuery's animate,
        });
    }
    /**
     * Open, close or toggle the main sidebar and lock page scrolling.
     *
     * @param switchOn
     *   - `true`  → ensure sidebar is open
     *   - `false` → ensure sidebar is closed
     *   - `undefined`/no arg → toggle current state
     */
    export function handleToggleSidebarVisibility(switchOn?: boolean): void {
        const sidebar = document.getElementById('main-sidebar-container');
        if (!sidebar) return;

        // Determine current vs desired state
        const isCurrentlyActive = sidebar.classList.contains('active');
        const shouldBeActive =
            switchOn === undefined ? !isCurrentlyActive : switchOn;

        // Apply the classes
        sidebar.classList.toggle('active', shouldBeActive);
        //document.body.classList.toggle('no-scroll', shouldBeActive);
    }

}